import { Typography, useTheme } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { ProductionType, TimeAggregation } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import { BarCell } from "../BarCell";
import { defaultTableSx } from "../tableFormatting";
import {
  GroupedProductionSeries,
  ProductionDenominator,
  ProductionSource,
} from "../types";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";
import { typeSafeObjectKeys } from "src/utils/typeSafeObjectKeys";
import { useTranslation } from "react-i18next";

type ProductionRow = {
  id: number;
  productGroup: string;
} & Record<`${ProductionSource}_mass`, number> &
  Record<`${ProductionSource}_heats`, number>;

interface ProductionTableProps {
  productionSeries: GroupedProductionSeries;
  selectedTimeAggregation: TimeAggregation;
  selectedProductionType: ProductionType;
  selectedProductionDenominator: ProductionDenominator;
  selectedDate: Date | null;
  selectedProductionSources: ProductionSource[];
}

export const ProductionTable = ({
  productionSeries,
  selectedDate,
  selectedTimeAggregation,
  selectedProductionType,
  selectedProductionDenominator,
  selectedProductionSources,
}: ProductionTableProps) => {
  const { t } = useTranslation();
  const { t: tenantT } = useTenantTranslation();
  const availableProductionSources = useMemo(
    () => selectedProductionSources,
    [selectedProductionSources]
  );

  const rows = useMemo(
    () =>
      selectedDate === null
        ? []
        : buildRows(
            productionSeries,
            selectedDate,
            selectedTimeAggregation,
            selectedProductionType,
            selectedProductionSources
          ),
    [
      productionSeries,
      selectedDate,
      selectedTimeAggregation,
      selectedProductionType,
      selectedProductionSources,
    ]
  );

  const totalsRow = useMemo(
    () => buildTotalsRow(rows, tenantT),
    [rows, tenantT]
  );

  const columns = useBuildColumns(
    availableProductionSources,
    rows,
    totalsRow,
    selectedProductionDenominator,
    t,
    tenantT
  );

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <HotkeyCompliantDataGrid
        sx={defaultTableSx}
        rows={rows}
        pinnedRows={{
          bottom: [totalsRow],
        }}
        columns={columns}
      />
    </div>
  );
};

const buildRows = (
  productionSeries: GroupedProductionSeries,
  selectedDate: Date,
  selectedTimeAggregation: TimeAggregation,
  selectedProductionType: ProductionType,
  selectedProductionSources: ProductionSource[]
): ProductionRow[] => {
  const rows: ProductionRow[] = [];

  let rowId = 0;

  productionSeries.availableProductGroups.forEach((productGroup) => {
    const filteredSeries = productionSeries.items.filter(
      ([key]) =>
        key.timeAggregation === selectedTimeAggregation &&
        key.productionType === selectedProductionType &&
        key.productGroup === productGroup &&
        selectedProductionSources.includes(key.productionSource)
    );
    const productionSourceValues = typeSafeObjectFromEntries(
      filteredSeries.flatMap(
        ([key, series]): [
          `${ProductionSource}_mass` | `${ProductionSource}_heats`,
          number,
        ][] => {
          const thisDateIndex = series.date.findIndex(
            (date) =>
              new Date(date).toISOString() === selectedDate.toISOString()
          );
          const mass =
            thisDateIndex === -1
              ? 0
              : (series.produced_mass[thisDateIndex] ?? 0);
          const heats =
            thisDateIndex === -1 ? 0 : (series.heat_count[thisDateIndex] ?? 0);
          return [
            [`${key.productionSource}_mass`, mass],
            [`${key.productionSource}_heats`, heats],
          ];
        }
      )
    );
    rows.push({
      id: rowId,
      productGroup,
      ...productionSourceValues,
    });
    rowId++;
  });
  return rows;
};

const buildTotalsRow = (rows: ProductionRow[], t: TFunction): ProductionRow => {
  const firstRow = rows[0];
  if (firstRow === undefined) {
    return { id: 0, productGroup: t("Total") };
  }
  const totals = typeSafeObjectFromEntries(
    typeSafeObjectKeys(firstRow)
      .filter((column) => column.endsWith("_mass") || column.endsWith("_heats"))
      .map((column) => [
        column as `${ProductionSource}_mass` | `${ProductionSource}_heats`,
        rows.reduce((sum, row) => sum + (Number(row[column]) || 0), 0),
      ])
  );
  return {
    id: rows.length,
    productGroup: t("Total"),
    ...totals,
  };
};

const useBuildColumns = (
  availableProductionSources: ProductionSource[],
  rows: ProductionRow[],
  totalsRow: ProductionRow,
  selectedProductionDenominator: ProductionDenominator,
  t: TFunction,
  tenantT: TFunction
): GridColDef[] => {
  const units = useUnitsFormatter(true);
  const theme = useTheme();

  const formatNumber = useFormatNumber();

  const perHeat = selectedProductionDenominator === "perHeat";

  const columns: GridColDef<ProductionRow>[] = [
    {
      field: "productGroup",
      headerName: tenantT("productGroup"),
      flex: 2,
      valueFormatter: (value?: string) => {
        const translated = !value ? "" : t(value);
        return translated;
      },
    },
    ...availableProductionSources.map((source) => {
      const displayedValue = (row: ProductionRow): number | null => {
        const mass = row[`${source}_mass`] ?? 0;
        if (!perHeat) {
          return mass;
        }
        const heats = row[`${source}_heats`] ?? 0;
        return heats > 0 ? mass / heats : null;
      };
      // Per-heat averages don't sum, so the summed-mass total is not a
      // meaningful bar ceiling; scale bars to the largest per-group value
      // instead.
      const barMaxValue = perHeat
        ? Math.max(
            0,
            ...rows
              .map((row) => displayedValue(row))
              .filter((value): value is number => value !== null)
          )
        : (totalsRow[`${source}_mass`] ?? 0);
      return {
        field: `${source}_mass`,
        type: "number" as const,
        headerName: perHeat
          ? `${t(source)}${units("mass")} / ${t("heat")}`
          : `${t(source)}${units("mass")}`,
        flex: 2,
        align: "right" as const,
        renderCell: (params: GridRenderCellParams<ProductionRow, number>) => {
          const value = displayedValue(params.row);
          const formattedValue =
            value === null ? "" : formatNumber(0, true)(value);
          return params.row.id === rows.length ? (
            <Typography variant="body1" component="div">
              {formattedValue}
            </Typography>
          ) : (
            <BarCell
              value={value ?? 0}
              formattedValue={formattedValue}
              maxValue={barMaxValue}
              color={theme.palette.data.blue}
            />
          );
        },
      };
    }),
  ];

  return columns;
};
